import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AbilityContext,
  AppAbility,
  createAbility,
  derivePermissionsFromRoles,
} from 'src/lib/ability';
import { getAllRoles } from 'src/services/roles.service';
import { getProfile } from 'src/services/users.service';
import { getFreshToken } from 'src/utils/get-fresh-token';
import { useAppSelector } from 'src/hooks/useStore';

/**
 * Resolve role IDs (e.g. "rol_Li6q17nBhAG6MOO0") to lowercase role names
 * (e.g. "staff") using the roles API. Role strings that are already names
 * are passed through unchanged.
 */
async function resolveRoleNames(
  roleIds: string[],
  token: string,
): Promise<string[]> {
  // If none look like Auth0 role IDs, nothing to resolve.
  if (!roleIds.some(r => r.startsWith('rol_'))) {
    return roleIds.map(r => r.toLowerCase());
  }
  try {
    const { roles = [] } = await getAllRoles({ page: 0, per_page: 100 }, token);
    const idToName = new Map(roles.map(r => [r.id, r.name.toLowerCase()]));
    return roleIds.map(r =>
      r.startsWith('rol_') ? (idToName.get(r) ?? r) : r.toLowerCase(),
    );
  } catch {
    return roleIds.map(r => r.toLowerCase());
  }
}

interface AbilityProviderProps {
  children: ReactNode;
}

export const AbilityLoadingContext = createContext<boolean>(true);

// Auth0 namespaces where permissions/roles may be embedded in the ID token.
const AUTH0_NS = 'https://dev-sitk24ss2iowl228.eu.auth0.com';

function extractStringArray(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

/** Safe subset of role-derived permissions when merging with external_permissions. */
function filterSupplementalRolePermissions(rolePerms: string[]): string[] {
  return rolePerms.filter(
    p =>
      p === 'manage:all' ||
      p.startsWith('read:sessions:') ||
      p.startsWith('assign:sessions:') ||
      p.startsWith('create:sessions:') ||
      p === 'read:flows:management' ||
      p.startsWith('read:flows:') ||
      p.startsWith('create:flows:') ||
      p.startsWith('create:flow:') ||
      p.startsWith('read:flow:') ||
      p.startsWith('read:developers:') ||
      p.startsWith('update:developers:') ||
      p.startsWith('manage:developers:') ||
      p.toLowerCase().startsWith('read:devhub:') ||
      p.toLowerCase().startsWith('edit:devhub:'),
  );
}

function mergeWithRolePermissions(
  permissions: string[],
  resolvedRoleNames: string[],
): string[] {
  const rolePerms = derivePermissionsFromRoles(resolvedRoleNames);
  if (rolePerms.includes('manage:all')) {
    return [...new Set([...permissions, 'manage:all'].filter(Boolean))];
  }
  return [
    ...new Set(
      [...permissions, ...filterSupplementalRolePermissions(rolePerms)].filter(
        Boolean,
      ),
    ),
  ];
}

export const AbilityProvider = ({ children }: AbilityProviderProps) => {
  const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();
  const { liveData } = useAppSelector(state => state.sliceTransaction);
  const [ability, setAbility] = useState<AppAbility>(createAbility([]));
  const [isAbilityLoading, setIsAbilityLoading] = useState(true);

  useEffect(() => {
    const updateAbility = async () => {
      if (isLoading) return;

      setIsAbilityLoading(true);

      if (!isAuthenticated) {
        setAbility(createAbility([]));
        setIsAbilityLoading(false);
        return;
      }

      try {
        const claims = await getIdTokenClaims();

        // ── Roles ─────────────────────────────────────────────────────────
        const roles = extractStringArray(
          claims?.['user/roles'] ??
            claims?.['roles'] ??
            claims?.[`${AUTH0_NS}/roles`],
        );

        // ── Permissions ───────────────────────────────────────────────────
        // Auth0 can include a `permissions` array in the ID token when the
        // "Add Permissions in the Access Token" option is also mirrored via
        // a custom Action/Rule into the ID token.
        const permissions = extractStringArray(
          claims?.['permissions'] ??
            claims?.['user/permissions'] ??
            claims?.[`${AUTH0_NS}/permissions`],
        );

        // Merge CMS profile permissions. Auth0 often sends role *IDs* (`rol_…`)
        // in the token instead of role names, so ROLE_PERMISSIONS never matches
        // and the user would have zero abilities — e.g. no Flows / ID Upload.
        let mergedPermissions = [...permissions];
        try {
          const token = await getFreshToken(getIdTokenClaims);
          const resolvedJwtRoles = token
            ? await resolveRoleNames(roles, token)
            : roles.map(r => r.toLowerCase());
          mergedPermissions = mergeWithRolePermissions(
            mergedPermissions,
            resolvedJwtRoles,
          );

          if (token) {
            const profile = await getProfile(token, liveData);
            mergedPermissions.push(
              ...(Array.isArray(profile.external_permissions)
                ? profile.external_permissions
                : []),
            );
            // JWT often contains role IDs (rol_…) instead of role names,
            // so ROLE_PERMISSIONS never matches. Resolve IDs → names via the
            // roles API, then derive permissions from those names.
            // IMPORTANT: only add a safe subset of role-derived permissions —
            // do not blindly merge all role permissions, as that would grant
            // subjects (e.g. Users) that external_permissions intentionally excludes.
            // Superadmin (`manage:all`) is always preserved so full access works.
            const rawProfileRoles = Array.isArray(profile.roles)
              ? profile.roles
              : [];
            const resolvedProfileRoles = await resolveRoleNames(
              rawProfileRoles,
              token,
            );
            mergedPermissions = mergeWithRolePermissions(
              mergedPermissions,
              resolvedProfileRoles,
            );

            let provisional = createAbility(
              resolvedJwtRoles,
              mergedPermissions.length > 0 ? mergedPermissions : undefined,
            );
            // Restricted client testers: assigned flows in CMS but no token/CMS
            // permission that maps to Home — grant minimal profile read so the
            // shell + ID Upload (gated on `read Home`) work. Does not grant `Users`.
            if (
              !provisional.can('read', 'Home') &&
              Array.isArray(profile.flowids) &&
              profile.flowids.length > 0
            ) {
              mergedPermissions.push('read:users:profile');
              mergedPermissions = [...new Set(mergedPermissions)];
              provisional = createAbility(
                resolvedJwtRoles,
                mergedPermissions.length > 0 ? mergedPermissions : undefined,
              );
            }
            setAbility(provisional);
            return;
          }
        } catch {
          // Profile optional; fall through to JWT-only ability
        }

        // JWT-only fallback: resolve role IDs and merge role-derived permissions.
        const token = await getFreshToken(getIdTokenClaims);
        const resolvedJwtRoles = token
          ? await resolveRoleNames(roles, token)
          : roles.map(r => r.toLowerCase());
        const roleDerived = derivePermissionsFromRoles(resolvedJwtRoles);
        const finalPermissions =
          permissions.length > 0
            ? mergeWithRolePermissions(permissions, resolvedJwtRoles)
            : roleDerived.length > 0
              ? roleDerived
              : permissions;
        setAbility(
          createAbility(
            resolvedJwtRoles,
            finalPermissions.length > 0 ? finalPermissions : undefined,
          ),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[CASL] Error building ability:', error);
        setAbility(createAbility([]));
      } finally {
        setIsAbilityLoading(false);
      }
    };

    updateAbility();
  }, [getIdTokenClaims, isAuthenticated, isLoading, liveData]);

  return (
    <AbilityContext.Provider value={ability}>
      <AbilityLoadingContext.Provider value={isAbilityLoading || isLoading}>
        {children}
      </AbilityLoadingContext.Provider>
    </AbilityContext.Provider>
  );
};
